"use client";

import { useState, useEffect, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '@/contexts/AuthContext';
import { isStaffRole } from '@/lib/roles';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import Link from 'next/link';
import { stampLoginSuccess } from '@/lib/perf/shellChain';

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';

// Toggled off 2026-08-19 per user request, pending Google Cloud Console OAuth
// consent screen branding/verification approval. Backend wiring (loginWithGoogle,
// /auth/callback, Google identity linking) stays fully intact — this only hides
// the button. Flip back to true once the user says the console approval landed.
const GOOGLE_AUTH_ENABLED = false;

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.348 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  );
}

const REMEMBER_ME_KEY = 'vfe:remember-me';

export default function Login() {
  const { loginWithEmail, loginWithGoogle, sendPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Soft nudge only, never a lockout — the password field stays live no matter
  // how many attempts fail. Resets on a successful login or a full page reload
  // (in-memory only, not persisted); unrelated to verifyPinGate's hard 5-attempt
  // /15-min throttle in actions/auth.ts, which this deliberately does not copy.
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const captcha = useRef<TurnstileInstance>(null);

  // Wave 1 only surfaces the control; Wave 2 wires the actual session-persistence
  // duration (short vs long-lived refresh) to this preference.
  useEffect(() => {
    const stored = localStorage.getItem(REMEMBER_ME_KEY);
    if (stored !== null) setRememberMe(stored === 'true');
    const err = new URLSearchParams(window.location.search).get('error');
    if (err === 'no_account') {
      setError('That Google email is not on the staff or student list yet. Request access, then sign in with Google after you are approved.');
    } else if (err === 'oauth') {
      setError('Google sign-in did not finish. Use the same browser you started in, or open the box from the usual address and try again.');
    }
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password.');
      return;
    }
    if (TURNSTILE_SITEKEY && !captchaToken) {
      setError('Complete the CAPTCHA challenge first.');
      return;
    }
    localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe));
    setLoading(true);
    // Password is intentionally not trimmed — spaces are part of the credential.
    const submittedAt = Date.now();
    const result = await loginWithEmail(email.trim(), password, captchaToken);
    if (result.success) {
      stampLoginSuccess({ submittedAt, actionMs: Date.now() - submittedAt, role: result.role });
      setFailedAttempts(0);
      const dest = isStaffRole(result.role) ? '/staff/dashboard' : '/student/dashboard';
      // Session cookies are set on the Server Action response (loginWithPassword).
      // Hard-navigate only after that promise resolves so proxy.ts sees them.
      // Leave `loading` true — the page is about to unload.
      window.location.assign(dest);
    } else {
      // Only burn the (single-use) CAPTCHA token and re-challenge on a real
      // failure — resetting after success needlessly re-locks the button into
      // "Verifying…" right as the redirect is in flight, which reads as a
      // second required click.
      captcha.current?.reset();
      setCaptchaToken(undefined);
      setLoading(false);
      setError(result.error || 'Invalid email or password.');
      setFailedAttempts(n => n + 1);
    }
  }

  async function handleForgotPassword() {
    if (!email.trim()) { setError('Enter your email above first, then click "Forgot password?".'); return; }
    if (TURNSTILE_SITEKEY && !captchaToken) {
      setError('Complete the CAPTCHA challenge first.');
      return;
    }
    setError('');
    setLoading(true);
    const result = await sendPasswordReset(email.trim(), captchaToken);
    captcha.current?.reset();
    setCaptchaToken(undefined);
    setLoading(false);
    if (result.success) setResetSent(true);
    else setError(result.error || 'Could not send reset email.');
  }

  async function handleGoogle() {
    setError('');
    setLoading(true);
    const result = await loginWithGoogle();
    if (!result.success) {
      setLoading(false);
      setError(result.error || 'Google sign-in failed.');
    }
  }

  return (
    <div className="auth-backdrop min-h-screen flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-[var(--bg-surface)] rounded-2xl shadow-xl overflow-hidden">
          <div className="px-8 pt-10 pb-6" style={{ backgroundColor: '#121212' }}>
            <div className="flex flex-col items-center gap-3">
              <img src="/brand/puma-filmstrip-transparent.png" alt="" className="w-16 h-16 object-contain" />
              <div className="text-center">
                <div className="text-white text-xl font-bold tracking-tight">Bunker Box</div>
                <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--amber)' }}>Equipment Management</div>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <p className="text-center text-sm text-[var(--text-2)] font-medium mb-6">Sign in to the box</p>
            <form onSubmit={handleLogin} className="space-y-4" autoComplete={rememberMe ? 'on' : 'off'}>
              <div>
                <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">Email</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full border border-[var(--border)] rounded-lg pl-9 pr-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                    autoComplete={rememberMe ? 'email' : 'off'}
                    autoFocus
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full border border-[var(--border)] rounded-lg pl-9 pr-9 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                    autoComplete={rememberMe ? 'current-password' : 'off'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-[var(--text-1)] transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-[var(--text-2)] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="rounded border-[var(--border)] focus:ring-2 focus:ring-brand"
                />
                Remember me on this device
              </label>

              {resetSent && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 text-sm text-green-700 font-medium dark:bg-green-950/40 dark:border-green-800 dark:text-green-300">
                  Reset link sent — check your email.
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600 font-medium dark:bg-red-950/40 dark:border-red-800 dark:text-red-200">
                  {error}
                </div>
              )}

              {failedAttempts >= 5 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800 font-medium dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200">
                  Still having trouble? Use Sign in with Google, or reset your password.
                </div>
              )}

              {TURNSTILE_SITEKEY ? (
                <div className="flex justify-center">
                  <Turnstile
                    ref={captcha}
                    siteKey={TURNSTILE_SITEKEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(undefined)}
                  />
                </div>
              ) : null}

              <div className="text-right -mt-2">
                <button type="button" onClick={handleForgotPassword} className="text-xs font-medium hover:underline" style={{ color: 'var(--amber)' }}>
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading || (!!TURNSTILE_SITEKEY && !captchaToken)}
                className="w-full py-3 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
              >
                {loading
                  ? <><Loader2 size={16} className="animate-spin" /> Signing in…</>
                  : (TURNSTILE_SITEKEY && !captchaToken)
                    ? <><Loader2 size={16} className="animate-spin" /> Verifying…</>
                    : 'Sign In'}
              </button>

              {GOOGLE_AUTH_ENABLED && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-[var(--border)]" />
                    <span className="text-[10px] text-[var(--text-3)] uppercase tracking-wide">Or</span>
                    <div className="flex-1 h-px bg-[var(--border)]" />
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogle}
                    disabled={loading}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 border border-[var(--border)] text-[var(--text-1)] hover:bg-[var(--bg-raised)]"
                  >
                    <GoogleMark /> Sign in with Google
                  </button>
                </>
              )}

              <a
                href="/request-access"
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-[var(--border)] text-[var(--text-1)] hover:bg-[var(--bg-raised)]"
              >
                Request Access
              </a>
            </form>
          </div>

          <div className="px-8 py-3 text-center bg-[var(--bg-raised)]">
            <Link href="/changelog" className="text-xs font-medium hover:underline block" style={{ color: 'var(--amber)' }}>
              Product updates
            </Link>
            <a
              href="/kiosk"
              onClick={() => typeof window !== 'undefined' && localStorage.setItem('kiosk_mode', 'true')}
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--amber)' }}
            >
              Switch to Kiosk Mode →
            </a>
            <p className="text-[10px] mt-1 text-[var(--text-3)]">Bunker Box</p>
          </div>
        </div>
      </div>
    </div>
  );
}
